import React from "react";
import { useForm } from "react-hook-form";
import { observer } from "mobx-react";
import {
  Box,
  Button,
  Divider,
  HStack,
  Input,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useStore } from "@/store";
import { translations } from "@/constants/translations";
import type { OrderDetails } from "@/store/types/order";
import { OrderSummaryCard } from "../Summary/OrderSummaryCard";
import { useAppNavigate } from "@/routing/useAppNavigate";
import { stripColon } from "@/helpers/string";
import { H3 } from "@/components/Heading";
import { FormInput } from "@/components/Form/FormInput";
import { OrderAddressTable } from "./OrderAddressTable";
import type { OrderContactAddressDto } from "@/api/types";
import { NewShippingAddressForm } from "./NewShippingAddressForm";
import { renderInnerHtml } from "@/helpers/dom";
import { MailToLink } from "@/components/Link";
import { NewBillingAddressForm } from "./NewBillingAddressForm";
import { telemetry } from "@/telemetry";

let idCounter = 1;

export const OrderShippingEmergentDetailsPanel = observer(() => {
  const {
    activeLanguage,
    orderData,
    setOrderData,
    orderContactData,
    setOrderContactData,
    ga4Store,
  } = useStore();
  const language = translations.pages["order-summary"][activeLanguage];
  const accountLanguage =
    translations.pages["emergent-account"][activeLanguage];
  const navigate = useAppNavigate();

  const onSubmit = (values: OrderDetails) => {
    if (missingFields.length > 0) {
      telemetry.trackEvent("shipping_validation_failed", {
        failed_fields: missingFields.join(","),
        count: missingFields.length,
      });
      return;
    }

    setOrderData({
      ...orderData,
      orderReference: values.orderReference,
    });
    telemetry.trackEvent("shipping_submitted", {
      same_as_billing: orderData.sameAsBilling,
      manual_address: !!orderData.isManualAddress,
      save_shipping: orderData.saveShippingAddress,
      save_billing: orderData.saveBillingAddress,
    });
    ga4Store.addShippingInfo();
    navigate({
      to: "/order/summary",
    });
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: orderData,
  });

  const handleBillingAddressSelect = (data: OrderContactAddressDto) => {
    setOrderData({
      ...orderData,
      billingAddressId: data.id,
      billingName: data.name,
      billingBusinessName: data.businessName,
      billingStreetAddress: data.streetAddress,
      billingOptionalUnit: data.unitSuiteEtc,
      billingProvince: data.province,
      billingCity: data.city,
      billingPostalCode: data.postalCode,
      billingAllowPo: data.allowPo,
    });
  };

  const handleShippingAddressSelect = (data: OrderContactAddressDto) => {
    setOrderData({
      ...orderData,
      shippingAddressId: data.id,
      shippingName: data.name,
      shippingBusinessName: data.businessName,
      shippingStreetAddress: data.streetAddress,
      shippingOptionalUnit: data.unitSuiteEtc,
      shippingProvince: data.province,
      shippingCity: data.city,
      shippingPostalCode: data.postalCode,
    });
  };

  const handleNewShippingAddressSave = (data: OrderDetails) => {
    if (!orderContactData) return;

    const newId = idCounter++;

    setOrderData({
      ...orderData,
      sameAsBilling: data.sameAsBilling,
      shippingAddressId: newId,
      shippingName: data.shippingName,
      shippingBusinessName: data.shippingBusinessName,
      shippingStreetAddress: data.shippingStreetAddress,
      shippingOptionalUnit: data.shippingOptionalUnit,
      shippingProvince: data.shippingProvince,
      shippingCity: data.shippingCity,
      shippingPostalCode: data.shippingPostalCode,
      saveShippingAddress: data.saveShippingAddress,
    });
    setOrderContactData({
      ...orderContactData!,
      shippingAddresses: [
        ...orderContactData.shippingAddresses,
        {
          id: newId,
          name: data.shippingName,
          businessName: data.shippingBusinessName,
          streetAddress: data.shippingStreetAddress,
          unitSuiteEtc: data.shippingOptionalUnit,
          province: data.shippingProvince,
          city: data.shippingCity,
          postalCode: data.shippingPostalCode,
          addressType: "Shipping",
          allowPo: data.billingAllowPo,
        },
      ],
    });
  };

  const handleNewBillingAddressSave = (data: OrderDetails) => {
    if (!orderContactData) return;

    const newId = idCounter++;

    setOrderData({
      ...orderData,
      billingAddressId: newId,
      billingName: data.billingName,
      billingBusinessName: data.billingBusinessName,
      billingStreetAddress: data.billingStreetAddress,
      billingOptionalUnit: data.billingOptionalUnit,
      billingProvince: data.billingProvince,
      billingCity: data.billingCity,
      billingPostalCode: data.billingPostalCode,
      billingAllowPo: orderData.billingAllowPo,
      saveBillingAddress: data.saveBillingAddress,
    });

    setOrderContactData({
      ...orderContactData!,
      billingAddresses: [
        ...orderContactData.billingAddresses,
        {
          id: newId,
          name: data.billingName,
          businessName: data.billingBusinessName,
          streetAddress: data.billingStreetAddress,
          unitSuiteEtc: data.billingOptionalUnit,
          province: data.billingProvince,
          city: data.billingCity,
          postalCode: data.billingPostalCode,
          addressType: "Billing",
          allowPo: orderData.billingAllowPo,
        },
      ],
    });
  };

  const missingFields = [
    ...(orderData.billingAddressId ? [] : ["billingAddressId"]),
    ...(orderData.shippingAddressId ||
    orderData.sameAsBilling ||
    orderData.shippingStreetAddress
      ? []
      : ["shippingAddressId"]),
  ];

  const allowSubmit = isValid && missingFields.length === 0;

  if (!orderContactData) return null;

  return (
    <Stack
      as="form"
      spacing="14"
      onSubmit={handleSubmit(onSubmit)}
    >
      <HStack
        alignItems="flex-start"
        spacing="14"
        flexDirection={{ sm: "column", md: "row" }}
      >
        <Stack
          spacing="14"
          flex="1"
          width="100%"
        >
          <Stack spacing="8">
            <H3>{language.contact_information}</H3>

            <SimpleGrid
              columns={{ sm: 1, md: 2 }}
              spacing="6"
            >
              <FormInput
                label={language.phone_no}
                placeholder="1-(xxx)-xxx-xxx"
                readOnly
                error={errors.phoneNumber}
                {...register("phoneNumber")}
              />
              <FormInput
                label={language.email_address}
                error={errors.emailAddress}
                readOnly
                {...register("emailAddress")}
              />
            </SimpleGrid>

            <Stack spacing="8">
              <H3>{language.enter_order_reference_number}</H3>
              <Input
                placeholder={stripColon(language.order_reference_number)}
                {...register("orderReference")}
              />
            </Stack>
          </Stack>
        </Stack>
        <OrderSummaryCard isFinalCost={false} />
      </HStack>

      <Stack spacing="8">
        <H3>{language.billing_address}</H3>

        <OrderAddressTable
          data={orderContactData.billingAddresses}
          selectedId={orderData.billingAddressId}
          onSelect={handleBillingAddressSelect}
        />
      </Stack>

      <NewBillingAddressForm onSubmit={handleNewBillingAddressSave} />

      <Stack spacing="8">
        <H3>{language.select_shipping_address}</H3>
        <OrderAddressTable
          data={orderContactData.shippingAddresses}
          selectedId={orderData.shippingAddressId}
          onSelect={handleShippingAddressSelect}
        />
      </Stack>

      <NewShippingAddressForm
        orderData={orderData}
        onSubmit={handleNewShippingAddressSave}
      />

      <Divider />

      <Box
        textAlign="center"
        width="100%"
      >
        <Text>
          {renderInnerHtml(language.edit_or_remove_shipping, [
            <Text
              key="1"
              as="span"
              color="primary-light"
              whiteSpace="nowrap"
            >
              {accountLanguage.phone}
            </Text>,
            <MailToLink
              color="primary-light"
              textDecoration="underline"
              key="2"
            >
              {accountLanguage.customer_email}
            </MailToLink>,
          ])}
        </Text>
      </Box>

      <HStack justifyContent="flex-end">
        <Button
          aria-disabled={!allowSubmit}
          type="submit"
        >
          {language.review_details}
        </Button>
      </HStack>
    </Stack>
  );
});
